/**
 * DeclineModal Component
 *
 * Modal for declining a booking request with reason selection.
 */

import React, { useState } from 'react';

interface DeclineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDecline: (reason: string, message?: string) => Promise<void>;
  isLoading: boolean;
  guestName: string;
}

const DECLINE_REASONS = [
  { id: 'dates_unavailable', label: 'Dates are no longer available' },
  { id: 'not_good_fit', label: 'Not a good fit for my property' },
  { id: 'incomplete_profile', label: 'Guest profile is incomplete' },
  { id: 'suspicious_request', label: 'Request seems suspicious' },
  { id: 'personal_use', label: 'I need the property for personal use' },
  { id: 'maintenance', label: 'Property needs maintenance' },
  { id: 'other', label: 'Other reason' },
];

export const DeclineModal: React.FC<DeclineModalProps> = ({
  isOpen,
  onClose,
  onDecline,
  isLoading,
  guestName,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  if (!isOpen) return null;

  const handleDecline = async () => {
    if (!selectedReason) return;
    await onDecline(selectedReason, customMessage || undefined);
    onClose();
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Decline Booking</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-amber-50 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <WarningIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">
                  Are you sure you want to decline?
                </p>
                <p className="text-sm text-amber-600 mt-1">
                  Declining too many requests may affect your listing's visibility.
                </p>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Please select a reason for declining{' '}
            <span className="font-medium">{guestName}'s</span> booking request:
          </p>

          <div className="space-y-2 mb-4">
            {DECLINE_REASONS.map((reason) => (
              <label
                key={reason.id}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  selectedReason === reason.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="declineReason"
                  value={reason.id}
                  checked={selectedReason === reason.id}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{reason.label}</span>
              </label>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message to guest (optional)
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a personal message explaining your decision..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              This message will be sent to the guest along with your decline reason.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDecline}
            disabled={isLoading || !selectedReason}
            className="flex-1 py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Declining...' : 'Decline Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
      clipRule="evenodd"
    />
  </svg>
);

export default DeclineModal;
